import { envelope, noiseBuffer } from './synth';

/**
 * Background music for the TV: a small generative loop synthesized with Web Audio (no recordings, nothing
 * to license). Each mood is a four-bar pattern on an eighth-note grid: soft pad chords, a round bass, a
 * light kit (kick, brushed snare, hats) and a sparse marimba-ish melody picked from the chord's own notes
 * by a seeded random walk, so it never repeats exactly but never clashes either.
 *
 *  - lobby: 92 bpm, Fmaj7 Em7 Dm7 Cmaj7, half-time kit, very sparse melody (chill).
 *  - game:  104 bpm, Am F C G, backbeat, bass on eighths, a quiet arpeggio (a little more upbeat).
 *  - final: a two-bar celebratory sting, then it settles back into the lobby loop on its own.
 *
 * Moods crossfade over two seconds; sound bites duck the whole bus briefly. A look-ahead scheduler
 * (`tick`) places notes on the audio clock, so timing never depends on the main thread being idle.
 */
/** lobby, a game (social), a 3D action game, the final results. */
export type Mood = 'lobby' | 'game' | 'action' | 'final';
export type MusicState = 'off' | 'muted' | 'waiting' | 'playing';

interface Pattern {
  bpm: number;
  /** One chord (MIDI notes) per bar. */
  chords: number[][];
  /** One bass root (MIDI) per bar. */
  roots: number[];
  /** Eight steps per bar. bass: x root, o octave, 5 fifth. kit: x hit, . rest. */
  bass: string;
  kick: string;
  snare: string;
  hat: string;
  /** Arpeggio on these steps (x), cycling the chord an octave up. */
  arp: string;
  /** Chance of a melody note on each step. */
  melody: number;
  /** Plays once, then hands over to `then`. */
  then?: Mood;
}

export const PATTERNS: Record<Mood, Pattern> = {
  lobby: {
    bpm: 92,
    chords: [[53, 57, 60, 64], [52, 55, 59, 62], [50, 53, 57, 60], [48, 52, 55, 59]],
    roots: [41, 40, 38, 36],
    bass: 'x.....5.', kick: 'x.......', snare: '....x...', hat: '..x...x.', arp: '........', melody: 0.16,
  },
  game: {
    bpm: 104,
    chords: [[57, 60, 64, 67], [53, 57, 60, 65], [55, 60, 64, 67], [55, 59, 62, 67]],
    roots: [45, 41, 48, 43],
    bass: 'x.xox.xo', kick: 'x...x...', snare: '..x...x.', hat: '.x.x.x.x', arp: 'x.x.x.x.', melody: 0.12,
  },
  action: {
    bpm: 126,
    chords: [[57, 60, 64, 69], [53, 57, 60, 65], [55, 59, 62, 67], [52, 56, 59, 64]],
    roots: [45, 41, 43, 40],
    bass: 'xoxox.xo', kick: 'x.x.x.x.', snare: '..x...x.', hat: 'xxxxxxxx', arp: 'xxxxxxxx', melody: 0.1,
  },
  final: {
    bpm: 116,
    chords: [[60, 64, 67, 72], [55, 59, 62, 67]],
    roots: [48, 43],
    bass: 'x.x.x.xo', kick: 'x.x.x.x.', snare: '....x.xx', hat: 'xxxxxxxx', arp: 'xxxxxxxx', melody: 0,
    then: 'lobby',
  },
};

const STEPS = 8;
const LOOKAHEAD = 0.35;
const FADE = 2;
/** Full-scale music level: modest under the sound bites even at 100 %. */
const MAX_LEVEL = 0.55;
export const DEFAULT_MUSIC_VOLUME = 0.35;

const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Layer {
  mood: Mood;
  pattern: Pattern;
  bus: GainNode;
  step: number;
  next: number;
  rng: () => number;
  /** Last melody note index (random walk over the chord). */
  walk: number;
  /** Context time this layer has faded out by (then it's dropped). */
  endsAt: number | null;
  /** A recorded track playing on this layer instead of the generated pattern. */
  audio?: HTMLAudioElement;
  track?: string;
}

/**
 * Recorded songs per mood (served by the web app; made with ElevenLabs Music). A mood without one falls back
 * to the generated loop. Each loops for as long as its stage lasts.
 */
export const TRACKS: Partial<Record<Mood, string>> = {
  lobby: '/music/lobby-lullaby.mp3',
  game: '/music/game-loop.mp3',
  action: '/music/action-loop.mp3',
  final: '/music/results.mp3',
};

export interface MusicOptions {
  /** The shared AudioContext (null until the browser allows audio). */
  context: () => BaseAudioContext | null;
  /** Run the scheduler on a timer (off in tests, which call `tick()` themselves). */
  timers?: boolean;
  /** Recorded songs per mood (default TRACKS; tests pass {} to hear the generated loops). */
  tracks?: Partial<Record<Mood, string>>;
}

export class MusicPlayer {
  private ctx: BaseAudioContext | null = null;
  private duckGain: GainNode | null = null;
  private volGain: GainNode | null = null;
  private layers: Layer[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();
  private requested: Mood = 'lobby';
  private seed = 1;
  /** The mood playing now (after a sting it reads 'lobby' again). */
  mood: Mood = 'lobby';
  enabled = false;
  muted = false;
  volume = DEFAULT_MUSIC_VOLUME;

  constructor(private opts: MusicOptions) {}

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }
  private emit() {
    for (const l of this.listeners) l();
  }

  get state(): MusicState {
    if (!this.enabled) return 'off';
    if (this.muted || this.volume <= 0) return 'muted';
    const c = this.opts.context();
    return c && c.state === 'running' ? 'playing' : 'waiting';
  }

  /** Moods active right now (the incoming one plus any still fading out). For tests and debugging. */
  activeLayers(): { mood: Mood; fading: boolean }[] {
    return this.layers.map((l) => ({ mood: l.mood, fading: l.endsAt !== null }));
  }

  /** The bus gains (duck, then volume), for tests. */
  get graph() {
    return { duck: this.duckGain, volume: this.volGain };
  }

  /** Whether this device should play music at all (the TV, with the host's setting on). */
  setEnabled(on: boolean) {
    if (this.enabled === on) return;
    this.enabled = on;
    this.sync();
  }

  setMuted(m: boolean) {
    if (this.muted === m) return;
    this.muted = m;
    this.sync();
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    this.sync();
  }

  /** Asks for a mood; asking again for the same one is a no-op (so the final sting plays once). */
  setMood(m: Mood) {
    if (m === this.requested) return;
    this.requested = m;
    // Already playing it (the lobby loop after the final sting): nothing to switch.
    if (m === this.mood && m !== 'final') return;
    // The same recorded song for both moods keeps playing rather than restarting.
    const live = this.layers.find((l) => l.endsAt === null);
    if (live?.track && live.track === this.trackFor(m)) {
      this.mood = m;
      live.mood = m;
      this.emit();
      return;
    }
    this.mood = m;
    const c = this.ctx;
    if (c && this.layers.some((l) => l.endsAt === null)) {
      this.fadeOutAll(c.currentTime);
      this.addLayer(m, c.currentTime + 0.05);
    }
    this.emit();
  }

  /** Dips the music under a sound bite: to (1 - depth) quickly, back up after `hold` seconds. */
  duck(depth: number, hold = 0.5) {
    const c = this.ctx;
    if (!c || !this.duckGain || depth <= 0) return;
    const p = this.duckGain.gain;
    const now = c.currentTime;
    p.cancelScheduledValues(now);
    p.setTargetAtTime(Math.max(0.05, 1 - depth), now, 0.04);
    p.setTargetAtTime(1, now + hold, 0.35);
  }

  /** Re-checks the context (call when it is created or resumed) and starts or stops the loop. */
  sync() {
    const want = this.enabled && !this.muted && this.volume > 0;
    const c = this.opts.context();
    if (c && c !== this.ctx) this.build(c);
    const ctx = this.ctx;
    if (ctx && this.volGain) {
      const p = this.volGain.gain;
      p.cancelScheduledValues(ctx.currentTime);
      p.setTargetAtTime(want ? this.volume * MAX_LEVEL : 0, ctx.currentTime, 0.15);
    }
    if (want && ctx && !this.layers.some((l) => l.endsAt === null)) this.addLayer(this.mood, ctx.currentTime + 0.05);
    // A song that was blocked before the first tap starts once the browser allows audio.
    if (want && ctx && ctx.state === 'running') for (const l of this.layers) if (l.audio && l.endsAt === null && l.audio.paused) void l.audio.play().catch(() => undefined);
    if (!want && ctx) this.fadeOutAll(ctx.currentTime, 0.6);
    if (this.opts.timers !== false) {
      if (want && !this.timer) this.timer = setInterval(() => this.tick(), 100);
      if (!want && this.timer && !this.layers.length) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
    this.emit();
  }

  private build(c: BaseAudioContext) {
    this.ctx = c;
    this.layers = [];
    this.volGain = c.createGain();
    this.volGain.gain.setValueAtTime(0, c.currentTime);
    this.duckGain = c.createGain();
    this.duckGain.gain.setValueAtTime(1, c.currentTime);
    this.duckGain.connect(this.volGain).connect(c.destination);
  }

  private trackFor(m: Mood): string | undefined {
    return (this.opts.tracks ?? TRACKS)[m];
  }

  private addLayer(mood: Mood, at: number) {
    const c = this.ctx!;
    const bus = c.createGain();
    bus.gain.setValueAtTime(0.0001, at);
    bus.gain.linearRampToValueAtTime(1, at + FADE);
    bus.connect(this.duckGain!);
    const layer: Layer = { mood, pattern: PATTERNS[mood], bus, step: 0, next: at, rng: mulberry32(this.seed++ * 7919), walk: 1, endsAt: null };
    const track = this.trackFor(mood);
    // A recorded song goes through the same fades and ducking; it needs a real AudioContext and <audio>.
    if (track && typeof Audio !== 'undefined' && 'createMediaElementSource' in c) {
      const audio = new Audio(track);
      audio.loop = true;
      audio.preload = 'auto';
      (c as AudioContext).createMediaElementSource(audio).connect(bus);
      void audio.play().catch(() => undefined);
      layer.audio = audio;
      layer.track = track;
    }
    this.layers.push(layer);
  }

  private fadeOutAll(now: number, fade = FADE) {
    for (const l of this.layers) {
      if (l.endsAt !== null) continue;
      const p = l.bus.gain;
      p.cancelScheduledValues(now);
      p.setValueAtTime(Math.max(0.0001, p.value), now);
      p.linearRampToValueAtTime(0.0001, now + fade);
      l.endsAt = now + fade;
    }
  }

  /** Schedules every step that starts within the look-ahead window. Safe to call as often as you like. */
  tick() {
    const c = this.ctx;
    if (!c || c.state !== 'running') return;
    const now = c.currentTime;
    const horizon = now + LOOKAHEAD;
    for (const l of this.layers) {
      if (l.audio) continue;
      while (l.next < horizon && (l.endsAt === null || l.next < l.endsAt)) {
        const stepDur = 60 / l.pattern.bpm / 2;
        this.playStep(c, l, l.step, l.next, stepDur);
        l.next += stepDur;
        l.step++;
        // A one-shot (the final sting) hands over to its follow-up mood on the same bus, in time.
        if (l.pattern.then && l.step >= l.pattern.chords.length * STEPS) {
          const nextMood = l.pattern.then;
          l.mood = nextMood;
          l.pattern = PATTERNS[nextMood];
          l.step = 0;
          if (l.endsAt === null) {
            this.mood = nextMood;
            this.emit();
          }
        }
      }
    }
    const before = this.layers.length;
    this.layers = this.layers.filter((l) => {
      if (l.endsAt === null || now < l.endsAt + 0.1) return true;
      l.audio?.pause();
      l.bus.disconnect();
      return false;
    });
    if (before && !this.layers.length && this.timer && !(this.enabled && !this.muted && this.volume > 0)) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private playStep(c: BaseAudioContext, l: Layer, step: number, t: number, stepDur: number) {
    const p = l.pattern;
    const s = step % STEPS;
    const bar = Math.floor(step / STEPS) % p.chords.length;
    const chord = p.chords[bar]!;
    const root = p.roots[bar]!;
    const out = l.bus;
    if (s === 0) this.pad(c, out, chord, t, stepDur * STEPS);
    const b = p.bass[s];
    if (b && b !== '.') this.bass(c, out, hz(root + (b === 'o' ? 12 : b === '5' ? 7 : 0)), t, stepDur * 1.6);
    if (p.kick[s] === 'x') this.kick(c, out, t);
    if (p.snare[s] === 'x') this.snare(c, out, t);
    if (p.hat[s] === 'x') this.hat(c, out, t, s % 2 ? 0.035 : 0.022);
    if (p.arp[s] === 'x') this.pluck(c, out, hz(chord[s % chord.length]! + 12), t, 0.045);
    if (p.melody > 0 && l.rng() < p.melody) {
      // A gentle random walk over the chord tones, two octaves up.
      l.walk = Math.max(0, Math.min(chord.length - 1, l.walk + (l.rng() < 0.5 ? -1 : 1)));
      this.pluck(c, out, hz(chord[l.walk]! + 24), t, 0.06);
    }
  }

  private pad(c: BaseAudioContext, out: AudioNode, chord: number[], t: number, dur: number) {
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(1300, t);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.05, t + 0.35);
    g.gain.setValueAtTime(0.05, t + dur - 0.3);
    g.gain.linearRampToValueAtTime(0, t + dur);
    f.connect(g).connect(out);
    for (const n of chord) {
      for (const det of [0.997, 1.003]) {
        const o = c.createOscillator();
        o.type = 'triangle';
        o.frequency.setValueAtTime(hz(n) * det, t);
        o.connect(f);
        o.start(t);
        o.stop(t + dur + 0.05);
      }
    }
  }

  private bass(c: BaseAudioContext, out: AudioNode, f: number, t: number, d: number) {
    const o = c.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(f, t);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(650, t);
    const g = c.createGain();
    envelope(g.gain, t, 0.24, 0.01, d);
    o.connect(lp).connect(g).connect(out);
    o.start(t);
    o.stop(t + d + 0.05);
  }

  private pluck(c: BaseAudioContext, out: AudioNode, f: number, t: number, peak: number) {
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f, t);
    const g = c.createGain();
    envelope(g.gain, t, peak, 0.004, 0.4);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + 0.45);
  }

  private kick(c: BaseAudioContext, out: AudioNode, t: number) {
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(130, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    const g = c.createGain();
    envelope(g.gain, t, 0.32, 0.004, 0.2);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + 0.22);
  }

  private snare(c: BaseAudioContext, out: AudioNode, t: number) {
    const src = c.createBufferSource();
    src.buffer = noiseBuffer(c);
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.setValueAtTime(1800, t);
    f.Q.setValueAtTime(0.8, t);
    const g = c.createGain();
    envelope(g.gain, t, 0.07, 0.003, 0.13);
    src.connect(f).connect(g).connect(out);
    src.start(t, 0.2);
    src.stop(t + 0.15);
  }

  private hat(c: BaseAudioContext, out: AudioNode, t: number, peak: number) {
    const src = c.createBufferSource();
    src.buffer = noiseBuffer(c);
    const f = c.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.setValueAtTime(7000, t);
    const g = c.createGain();
    envelope(g.gain, t, peak, 0.002, 0.035);
    src.connect(f).connect(g).connect(out);
    src.start(t, 0.6);
    src.stop(t + 0.05);
  }
}
